import React, { useEffect } from "react"
import {
    Box,
    Button,
    Divider,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { Save, Settings } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import {
    CAMPOS_TEXTO_PUBLICOS,
    CAMPOS_TEXTO_FRASES,
    GRUPOS_CHECKBOXES_PUBLICOS,
    GRUPOS_CHECKBOXES_CLIENTEFIEL,
} from "../models/parametrosAplicativoModel"
import type { ParametrosAplicativoModel } from "../models/parametrosAplicativoModel"
import { BotaoVoltar } from "./BotaoVoltar"
import { ParametrosAplicativoActionsTypes } from "../context/parametrosAplicativoActions"
import { useParametrosAplicativoContext } from "../context/useParametrosAplicativoContext"
import useAppStore from "../../../stores/AppStore"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: { paddingLeft: "20px", paddingRight: "20px", paddingTop: "80px" },
        [theme.breakpoints.down("sm")]: { paddingLeft: "15px", paddingRight: "15px", paddingTop: "70px" },
    },
    content: {
        margin: "auto",
        maxWidth: "900px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    switchRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        paddingTop: theme.spacing(0.5),
        paddingBottom: theme.spacing(0.5),
    },
    switchLabel: {
        color: "#2c3e50",
        fontSize: "0.95rem",
        flex: 1,
    },
    saveButton: {
        borderRadius: 25,
        backgroundColor: "#4caf50",
        color: "white",
        padding: theme.spacing(1.5, 4),
        fontWeight: 600,
        boxShadow: "0 4px 15px rgba(76,175,80,0.3)",
        "&:hover": { backgroundColor: "#43a047" },
    },
    somenteLeituraMsg: {
        color: "#e74c3c",
        fontWeight: 600,
        padding: theme.spacing(2),
        backgroundColor: "#ffeaa7",
        borderRadius: 8,
        marginBottom: theme.spacing(2),
    },
    clienteFielCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        borderLeft: "4px solid #1976d2",
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
}))

export function FormularioParametrosAplicativo() {
    const classes = useStyles()
    const { parametros, dispatch, salvar, erroCarregamento } = useParametrosAplicativoContext()
    const { enqueueSnackbar } = useSnackbar()

    const configuracoes = useAppStore(s => s.configuracoes)
    // "admin" (acesso total) = perfil CLIENTEFIEL; "administrador" = acesso restrito.
    const ehClienteFiel = configuracoes?.user?.perfil === "CLIENTEFIEL"
    const somenteLeitura = parametros.podeEditar === false
    const desabilitado = !!erroCarregamento || somenteLeitura

    useEffect(() => {
        if (erroCarregamento) enqueueSnackbar(erroCarregamento, { variant: "error" })
    }, [erroCarregamento]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleTexto = (campo: keyof ParametrosAplicativoModel, valor: string) => {
        dispatch({
            type: ParametrosAplicativoActionsTypes.ATUALIZAR_CAMPO,
            payload: { campo, valor: valor || null },
        })
    }

    const handleNumero = (campo: keyof ParametrosAplicativoModel, valor: number | null) => {
        dispatch({
            type: ParametrosAplicativoActionsTypes.ATUALIZAR_CAMPO,
            payload: { campo, valor },
        })
    }

    const handleBoolean = (campo: keyof ParametrosAplicativoModel, checked: boolean) => {
        dispatch({
            type: ParametrosAplicativoActionsTypes.ATUALIZAR_CAMPO,
            payload: { campo, valor: checked },
        })
    }

    const onSalvar = async () => {
        try {
            await salvar()
            enqueueSnackbar("Parâmetros salvos com sucesso!", { variant: "success" })
        } catch (e) {
            enqueueSnackbar((e as Error).message || "Erro ao salvar", { variant: "error" })
        }
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <BotaoVoltar />
                {somenteLeitura && (
                    <Typography className={classes.somenteLeituraMsg}>
                        Este estabelecimento não possui um aplicativo próprio. Os parâmetros são somente leitura.
                    </Typography>
                )}

                {/* Card: Rastreamento e Identificação */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Settings className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Rastreamento e Identificação
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Grid container spacing={3}>
                            {CAMPOS_TEXTO_PUBLICOS.map(({ campo, label, descricao }) => (
                                <Grid item xs={12} sm={6} md={4} key={campo}>
                                    <Tooltip title={descricao || ""} placement="top" arrow>
                                        <TextField
                                            label={label}
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            disabled={desabilitado}
                                            value={(parametros[campo] as string | null) ?? ""}
                                            onChange={e => handleTexto(campo, e.target.value)}
                                        />
                                    </Tooltip>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Paper>

                {/* Card: Configurações de Entrega e Endereço */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Settings className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Configurações de Entrega e Endereço
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl variant="outlined" size="small" fullWidth disabled={desabilitado}>
                                    <InputLabel>Categoria</InputLabel>
                                    <Select
                                        label="Categoria"
                                        value={parametros.categoria ?? ""}
                                        onChange={e => {
                                            const val = e.target.value
                                            handleNumero("categoria", val === "" ? null : Number(val))
                                        }}
                                    >
                                        <MenuItem value="">Não definido</MenuItem>
                                        <MenuItem value={0}>Outros</MenuItem>
                                        <MenuItem value={1}>Açaí</MenuItem>
                                        <MenuItem value={2}>Pizzaria</MenuItem>
                                        <MenuItem value={3}>Hamburgueria</MenuItem>
                                        <MenuItem value={4}>Bebidas</MenuItem>
                                        <MenuItem value={5}>Oriental</MenuItem>
                                        <MenuItem value={6}>Restaurante</MenuItem>
                                        <MenuItem value={7}>Supermercado</MenuItem>
                                        <MenuItem value={8}>Cafeteria</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl variant="outlined" size="small" fullWidth disabled={desabilitado}>
                                    <InputLabel>Tipo de endereço</InputLabel>
                                    <Select
                                        label="Tipo de endereço"
                                        value={parametros.tipoEndereco}
                                        onChange={e => handleNumero("tipoEndereco", Number(e.target.value))}
                                    >
                                        <MenuItem value={0}>Número (Padrão)</MenuItem>
                                        <MenuItem value={1}>Quadra e Lote</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl variant="outlined" size="small" fullWidth disabled={desabilitado}>
                                    <InputLabel>Tipo de seleção de endereço</InputLabel>
                                    <Select
                                        label="Tipo de seleção de endereço"
                                        value={parametros.tipoSelecaoEndereco}
                                        onChange={e => handleNumero("tipoSelecaoEndereco", Number(e.target.value))}
                                    >
                                        <MenuItem value={0}>Por CEP (Padrão)</MenuItem>
                                        <MenuItem value={1}>Google Maps</MenuItem>
                                        <MenuItem value={2}>MapBox</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl variant="outlined" size="small" fullWidth disabled={desabilitado}>
                                    <InputLabel>Definição de bairros</InputLabel>
                                    <Select
                                        label="Definição de bairros"
                                        value={parametros.tipoEntregaBairro}
                                        onChange={e => handleNumero("tipoEntregaBairro", Number(e.target.value))}
                                    >
                                        <MenuItem value={0}>Bairros Centralizados (Padrão)</MenuItem>
                                        <MenuItem value={1}>Bairros Específicos</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl variant="outlined" size="small" fullWidth disabled={desabilitado}>
                                    <InputLabel>Tipo de bloqueio</InputLabel>
                                    <Select
                                        label="Tipo de bloqueio"
                                        value={parametros.tipoBloqueio}
                                        onChange={e => handleNumero("tipoBloqueio", Number(e.target.value))}
                                    >
                                        <MenuItem value={0}>Apenas pagamentos online</MenuItem>
                                        <MenuItem value={1}>Todas as compras</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>

                {/* Cards de Checkboxes públicos */}
                {GRUPOS_CHECKBOXES_PUBLICOS.map((grupo, gi) => (
                    <Paper className={classes.configCard} key={gi}>
                        <Box className={classes.cardHeader}>
                            <Settings className={classes.iconHeader} />
                            <Typography variant="h6" className={classes.cardHeaderTitle}>
                                {grupo.titulo}
                            </Typography>
                        </Box>
                        <Box className={classes.cardContent}>
                            {grupo.campos.map(({ campo, label, descricao }, ci) => (
                                <React.Fragment key={campo}>
                                    {ci > 0 && <Divider />}
                                    <Tooltip title={descricao || ""} placement="top" arrow>
                                        <Box className={classes.switchRow}>
                                            <Typography className={classes.switchLabel}>{label}</Typography>
                                            <Switch
                                                color="primary"
                                                disabled={desabilitado}
                                                checked={!!parametros[campo]}
                                                onChange={e => handleBoolean(campo, e.target.checked)}
                                            />
                                        </Box>
                                    </Tooltip>
                                </React.Fragment>
                            ))}
                        </Box>
                    </Paper>
                ))}

                {/* ── Seção ClienteFiel ─────────────────────────────────── */}
                {ehClienteFiel && (
                    <>
                        {/* Card: Frases e Textos */}
                        <Paper className={classes.clienteFielCard}>
                            <Box className={classes.cardHeader}>
                                <Settings className={classes.iconHeader} />
                                <Typography variant="h6" className={classes.cardHeaderTitle}>
                                    Frases e Textos
                                </Typography>
                            </Box>
                            <Box className={classes.cardContent}>
                                <Grid container spacing={3}>
                                    {CAMPOS_TEXTO_FRASES.map(({ campo, label, multiline }) => (
                                        <Grid item xs={12} sm={multiline ? 12 : 6} md={multiline ? 12 : 4} key={campo}>
                                            <TextField
                                                label={label}
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                                multiline={multiline}
                                                rows={multiline ? 3 : undefined}
                                                disabled={desabilitado}
                                                value={(parametros[campo] as string | null) ?? ""}
                                                onChange={e => handleTexto(campo, e.target.value)}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        </Paper>

                        {/* Card: Indicação / Selos */}
                        <Paper className={classes.clienteFielCard}>
                            <Box className={classes.cardHeader}>
                                <Settings className={classes.iconHeader} />
                                <Typography variant="h6" className={classes.cardHeaderTitle}>
                                    Indicação e Selos
                                </Typography>
                            </Box>
                            <Box className={classes.cardContent}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <TextField
                                            label="Selos por indicação (Convidante)"
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            type="number"
                                            disabled={desabilitado}
                                            value={parametros.selosPorIndicacaoConvidante ?? 1}
                                            onChange={e => handleNumero("selosPorIndicacaoConvidante", e.target.value ? Number(e.target.value) : null)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <TextField
                                            label="Selos por indicação (Convidado)"
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            type="number"
                                            disabled={desabilitado}
                                            value={parametros.selosPorIndicacaoConvidado ?? 1}
                                            onChange={e => handleNumero("selosPorIndicacaoConvidado", e.target.value ? Number(e.target.value) : null)}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Paper>

                        {/* Cards de Checkboxes clientefiel */}
                        {GRUPOS_CHECKBOXES_CLIENTEFIEL.map((grupo, gi) => (
                            <Paper className={classes.clienteFielCard} key={`cf-${gi}`}>
                                <Box className={classes.cardHeader}>
                                    <Settings className={classes.iconHeader} />
                                    <Typography variant="h6" className={classes.cardHeaderTitle}>
                                        {grupo.titulo}
                                    </Typography>
                                </Box>
                                <Box className={classes.cardContent}>
                                    {grupo.campos.map(({ campo, label, descricao }, ci) => (
                                        <React.Fragment key={campo}>
                                            {ci > 0 && <Divider />}
                                            <Tooltip title={descricao || ""} placement="top" arrow>
                                                <Box className={classes.switchRow}>
                                                    <Typography className={classes.switchLabel}>{label}</Typography>
                                                    <Switch
                                                        color="primary"
                                                        disabled={desabilitado}
                                                        checked={!!parametros[campo]}
                                                        onChange={e => handleBoolean(campo, e.target.checked)}
                                                    />
                                                </Box>
                                            </Tooltip>
                                        </React.Fragment>
                                    ))}
                                </Box>
                            </Paper>
                        ))}
                    </>
                )}

                {/* Botão Salvar */}
                <Box display="flex" justifyContent="center" mt={2} mb={4}>
                    <Button
                        className={classes.saveButton}
                        variant="contained"
                        startIcon={<Save />}
                        onClick={onSalvar}
                        disabled={desabilitado}
                    >
                        Salvar Parâmetros
                    </Button>
                </Box>
            </Box>
        </Box>
    )
}
